/**
 * Projection Types for the frontend application
 *
 * These types define the data structures for the Projections feature,
 * including GM inputs, calculated seasonality distributions, and approval workflow.
 */

import { MonthlyTotals } from './index';

// ========================================
// PROJECTION STATUS & WORKFLOW
// ========================================

/**
 * Projection lifecycle status (date-based)
 * - draft: Within editing window (Oct 1 - Dec 31, ET) for this year, can be edited
 * - final: Outside editing window, locked for editing (executives can override)
 */
export type ProjectionStatus = 'draft' | 'final';

/**
 * Input type for projection values
 * - amount: Dollar value (used for Gross, Sales, Units)
 * - percentage: Percentage of department gross (used for Expenses)
 */
export type ProjectionInputType = 'amount' | 'percentage';

// ========================================
// EDIT LOCK
// ========================================

/**
 * Edit lock status
 */
export interface EditLockInfo {
  isLocked: boolean;
  lockedBy?: string;
  lockedByName?: string;
  lockedByEmail?: string;
  lockedAt?: string;
  expiresAt?: string;
  isCurrentUser: boolean;
}

// ========================================
// PROJECTION INPUT/OUTPUT TYPES
// ========================================

/**
 * Single projection input from the GM
 */
export interface ProjectionInput {
  department: string;
  metric: string;
  inputType: ProjectionInputType;
  value: number;
  calculatedAmount?: number; // For percentage inputs
  // Frontend-calculated monthly distribution (to be stored as CALCULATED record)
  monthlyDistribution?: MonthlyTotals;
  // Seasonality metadata
  seasonalityWeights?: MonthlyTotals;
  yearsUsedForWeights?: number[];
}

/**
 * Projection input record from API
 */
export interface ProjectionInputRecord {
  compositeKey: string;
  recordType: string;
  locationId: number;
  targetYear: number;
  department: string;
  metric: string;
  inputType: ProjectionInputType;
  value: number;
  calculatedAmount?: number;
  lastEditedBy: string;
  lastEditedByName: string;
  lastEditedAt: string;
}

/**
 * Calculated monthly distribution record from API
 */
export interface ProjectionCalculatedRecord {
  compositeKey: string;
  recordType: string;
  locationId: number;
  targetYear: number;
  department: string;
  metric: string;
  annualTotal: number;
  monthlyDistribution: MonthlyTotals;
  seasonalityWeights: MonthlyTotals;
  yearsUsedForWeights: number[];
  recencyWeights: Record<number, number>;
  calculatedAt: string;
  // Executive monthly adjustment (monthlyDistribution = baseDistribution + deltas).
  // Mirrors lambda/shared/projection-monthly-adjustments.ts.
  manualAdjustment?: ManualAdjustment;
}

/**
 * An Executive's month-by-month adjustment of a seasonality spread; the annual never changes.
 * `derived` marks TOTAL_GROSS / OPERATING_PROFIT records rewritten as a consequence.
 */
export interface ManualAdjustment {
  adjustedBy: string;
  adjustedByName: string;
  adjustedAt: string;
  baseDistribution: MonthlyTotals;
  deltas: MonthlyTotals;
  derived?: 'TOTAL_GROSS' | 'OPERATING_PROFIT' | 'GROSS';
}

/**
 * Projection metadata from API
 */
export interface ProjectionMetadata {
  status: ProjectionStatus;
  lastEditedBy?: string;
  lastEditedByName?: string;
  lastEditedAt?: string;
  // Executive unlock override (for editing outside the window)
  unlockOverrideBy?: string;
  unlockOverrideByName?: string;
  unlockOverrideAt?: string;
  unlockOverrideExpiresAt?: string; // 24-hour expiration
  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
}

/**
 * Editing window status information
 */
export interface EditingWindowStatus {
  isWithinWindow: boolean;
  hasExecutiveOverride: boolean;
  overrideBy?: string;
  overrideAt?: string;
  overrideExpiresAt?: string;
}

// ========================================
// HISTORICAL DATA FOR COMPARISON
// ========================================

/**
 * Raw historical year data - same format as get-historical-data API
 * Contains raw accounts, docGross, and unitCounts for processing
 */
export interface RawHistoricalYearData {
  year: number;
  isComplete: boolean;
  monthsWithData: number;
  // Raw account data with monthly breakdowns
  accounts: {
    account: string;
    description: string;
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  }[];
  // DOC gross by department
  docGross: {
    department: string;
    displayName: string;
    sales?: MonthlyTotals;
    cos?: MonthlyTotals;
    gross?: MonthlyTotals;
    ytdSales?: number;
    ytdCos?: number;
    ytdGross: number;
    monthlyGross?: MonthlyTotals;
  }[];
  // Unit counts
  unitCounts?: {
    newVehicleUnits: Record<string, number>;
    usedVehicleUnits: Record<string, number>;
    totalNewUnits: number;
    totalUsedUnits: number;
    monthlyNewUnits?: MonthlyTotals;
    monthlyUsedUnits?: MonthlyTotals;
    monthlyNewUnitsByBrand?: Record<string, MonthlyTotals>;
    monthlyUsedUnitsByBrand?: Record<string, MonthlyTotals>;
  };
}

// ========================================
// API RESPONSE TYPES
// ========================================

/**
 * Unit counts data by brand for vehicle departments
 */
export interface UnitCountsByBrand {
  newVehicleUnits: Record<string, number>;  // Brand -> count
  usedVehicleUnits: Record<string, number>; // Brand -> count
  totalNewUnits: number;
  totalUsedUnits: number;
  // Monthly breakdown by brand (optional)
  monthlyNewUnitsByBrand?: Record<string, MonthlyTotals>;
  monthlyUsedUnitsByBrand?: Record<string, MonthlyTotals>;
}

/**
 * Full projection data returned by get-projections API
 */
export interface ProjectionData {
  locationId: number;
  locationName: string;
  targetYear: number;
  status: ProjectionStatus;
  inputs: ProjectionInputRecord[];
  calculations: ProjectionCalculatedRecord[];
  metadata: ProjectionMetadata | null;
  // Raw historical data - same format as get-historical-data API
  // Keys are dynamic based on target year (e.g., year2023, year2024, year2025)
  historicalRaw: Record<string, RawHistoricalYearData | null>;
  unitCountsByBrand?: UnitCountsByBrand;
  lockStatus: EditLockInfo;
  // Editing permission based on date window + executive override
  canEdit: boolean;
  editingWindowStatus: EditingWindowStatus;
}

/**
 * Save projection request
 */
export interface SaveProjectionRequest {
  locationId: number;
  targetYear: number;
  inputs: ProjectionInput[];
}

/**
 * Lock operation request
 */
export interface LockRequest {
  locationId: number;
  targetYear: number;
}

/**
 * Lock operation response
 */
export interface LockOperationResponse {
  success: boolean;
  message: string;
  lockStatus: EditLockInfo;
  error?: string;
}

/**
 * Executive override request (for editing outside the window)
 */
export interface OverrideRequest {
  locationId: number;
  targetYear: number;
}

/**
 * Executive override response
 */
export interface OverrideResponse {
  success: boolean;
  message: string;
  canEdit: boolean;
  expiresAt?: string;
  error?: string;
}

// ========================================
// UI STATE TYPES
// ========================================

/**
 * Local state for projection inputs (before saving)
 */
export interface ProjectionInputState {
  [key: string]: {
    value: number;
    inputType: ProjectionInputType;
    isDirty: boolean;
  };
}

/**
 * Generate key for input state lookup
 */
export function getInputStateKey(department: string, metric: string): string {
  return `${department}#${metric}`;
}

// ========================================
// DEPARTMENT & METRIC DEFINITIONS
// ========================================

/**
 * Standard departments for projections
 */
export const PROJECTION_DEPARTMENTS = [
  'NEW_VEHICLE',
  'USED_VEHICLE',
  'SERVICE',
  'PARTS',
  'BODY_SHOP',
  'RENTAL',
  'OTHERS',
] as const;

export type ProjectionDepartment = typeof PROJECTION_DEPARTMENTS[number];

/**
 * Department display names
 */
export const DEPARTMENT_DISPLAY_NAMES: Record<string, string> = {
  'NEW_VEHICLE': 'New Vehicles',
  'USED_VEHICLE': 'Used Vehicles',
  'SERVICE': 'Service',
  'PARTS': 'Parts',
  'BODY_SHOP': 'Body Shop',
  'RENTAL': 'Rental',
  'OTHERS': 'Others',
};

/**
 * Metrics for New/Used Vehicle departments
 */
export const VEHICLE_DEPT_METRICS = [
  'SALES',
  'GROSS',
  'FI_INCOME',
  'UNITS',
  'VARIABLE_EXPENSES',
  'SEMI_FIXED_EXPENSES',
  'FIXED_EXPENSES',
  'OTHER_EXPENSES',
] as const;

/**
 * Metrics for Service/Parts/Body Shop departments
 */
export const SERVICE_DEPT_METRICS = [
  'SALES',
  'GROSS',
  'SALES_EXPENSES',
  'FIXED_EXPENSES',
  'OTHER_EXPENSES',
] as const;

/**
 * Check if a metric should use percentage input
 */
export function isPercentageMetric(metric: string): boolean {
  const percentageMetrics = [
    'VARIABLE_EXPENSES',
    'SEMI_FIXED_EXPENSES',
    'SALES_EXPENSES',
    'FIXED_EXPENSES',
    'OTHER_EXPENSES',
    'SELLING_EXPENSE',
    'FIXED_EXPENSE',
  ];
  return percentageMetrics.includes(metric);
}

/**
 * Get the display name for a metric
 */
export function getMetricDisplayName(metric: string): string {
  const names: Record<string, string> = {
    'SALES': 'Sales',
    'GROSS': 'Gross',
    'FI_INCOME': 'F&I Income',
    'TOTAL_GROSS': 'Total Gross',
    'UNITS': 'Units',
    'VARIABLE_EXPENSES': 'Variable Expenses',
    'SEMI_FIXED_EXPENSES': 'Semi-Fixed Expenses',
    'SALES_EXPENSES': 'Sales Expenses',
    'FIXED_EXPENSES': 'Fixed Expenses',
    'OTHER_EXPENSES': 'Other Expenses',
    'TOTAL_EXPENSES': 'Total Expenses',
    'OPERATING_PROFIT': 'Operating Profit',
    'INCOME': 'Income',
    'SELLING_EXPENSE': 'Selling Expense',
    'FIXED_EXPENSE': 'Fixed Expense',
    'OTHER_INCOME': 'Other Income',
  };
  return names[metric] || metric;
}

// ========================================
// EDITING WINDOW CONSTANTS
// ========================================
// MIRROR of lambda/shared/projection-types.ts - keep in sync. The shared
// boundary cases live in lambda/shared/projection-window.test.ts and
// frontend/src/types/projections.test.ts.

/**
 * Editing window: Oct 1 through Dec 31 (Eastern time) for the NEXT year's
 * projection. Locks at Jan 1 00:00 ET; after that only an Admin/CEO/Executive
 * 24h override allows edits. (Was Nov 1 - Jan 15 until the 2027 cycle.)
 */
export const EDITING_WINDOW = {
  START_MONTH: 10,  // October
  START_DAY: 1,
  END_MONTH: 12,    // December
  END_DAY: 31,
} as const;

/** Jan day of the target year from which seasonality uses t-1..t-3 */
export const FINAL_SEASONALITY_JAN_DAY = 10;

/** All projection date rules are evaluated in dealership (Eastern) time. */
export const PROJECTION_TIME_ZONE = 'America/New_York';

/**
 * First year projections were available (for archive dropdown)
 */
const ACTUAL_FIRST_PROJECTION_YEAR = 2026;

// =============================================
// TEMPORARY TEST FLAGS - SET ALL TO false/null FOR PRODUCTION
// To rollback: set TEST_OUTSIDE_WINDOW = false and TEST_FIRST_YEAR = null
// =============================================
const TEST_OUTSIDE_WINDOW = false;           // Simulates being outside editing window (Final status)
const TEST_FIRST_YEAR: number | null = null; // Adds earlier year to archive dropdown for testing
// =============================================

export const FIRST_PROJECTION_YEAR = TEST_FIRST_YEAR ?? ACTUAL_FIRST_PROJECTION_YEAR;

// ========================================
// DATE-BASED HELPER FUNCTIONS
// ========================================

/** Calendar parts of a date in Eastern time (independent of the browser's zone). */
export function easternDateParts(date: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PROJECTION_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * Get the target projection year based on the Oct 1 (ET) pivot.
 * - Oct-Dec: working on next year's projection (currentYear + 1)
 * - Jan-Sep: still showing current year's projection (currentYear)
 *
 * Examples: Sep 30, 2026 -> 2026; Oct 1, 2026 -> 2027; Jan 15, 2027 -> 2027
 */
export function getTargetProjectionYear(referenceDate?: Date): number {
  const { year, month } = easternDateParts(referenceDate || new Date());
  return month >= EDITING_WINDOW.START_MONTH ? year + 1 : year;
}

/**
 * Get the 3 historical years prior to the target year.
 * @deprecated Use getSeasonalityYears (weights) or getDisplayHistoricalYears (columns).
 */
export function getHistoricalYears(targetYear: number): [number, number, number] {
  return [targetYear - 3, targetYear - 2, targetYear - 1];
}

/** True until Jan 10 (ET) of the target year - seasonality is provisional. */
export function isSeasonalityProvisional(targetYear: number, referenceDate?: Date): boolean {
  const { year, month, day } = easternDateParts(referenceDate || new Date());
  if (year < targetYear) return true;
  if (year > targetYear) return false;
  return month === 1 && day < FINAL_SEASONALITY_JAN_DAY;
}

/**
 * The three years driving the 50/30/20 seasonality weights, NEWEST FIRST
 * (weights are applied positionally: index 0 gets 50%).
 * - Provisional (before Jan 10 of target): [t-2, t-3, t-4]
 * - Final: [t-1, t-2, t-3]
 */
export function getSeasonalityYears(targetYear: number, referenceDate?: Date): [number, number, number] {
  const newest = isSeasonalityProvisional(targetYear, referenceDate) ? targetYear - 2 : targetYear - 1;
  return [newest, newest - 1, newest - 2];
}

/**
 * Historical years shown as tabs/columns, ASCENDING: the seasonality years plus
 * the (possibly partial) year before the target.
 * - 2027 during the window: [2023, 2024, 2025, 2026]
 * - 2027 from Jan 10, 2027: [2024, 2025, 2026]
 */
export function getDisplayHistoricalYears(targetYear: number, referenceDate?: Date): number[] {
  const years = new Set<number>([...getSeasonalityYears(targetYear, referenceDate), targetYear - 1]);
  return Array.from(years).sort((a, b) => a - b);
}

/**
 * Months of a year with (substantially) complete data: 12 for past years,
 * current month - 1 for the current year, 0 for future years (ET).
 */
export function getMonthsClosed(year: number, referenceDate?: Date): number {
  const now = easternDateParts(referenceDate || new Date());
  if (year < now.year) return 12;
  if (year > now.year) return 0;
  return now.month - 1;
}

/** Determine if we are within the editing window (Oct 1 - Dec 31, ET). */
export function isWithinEditingWindow(referenceDate?: Date): boolean {
  // TEMPORARY: Simulate being outside editing window for testing
  if (TEST_OUTSIDE_WINDOW) return false;

  const { month } = easternDateParts(referenceDate || new Date());
  return month >= EDITING_WINDOW.START_MONTH;
}

/** The window only opens the upcoming target year (2026 is archived during the 2027 window). */
export function isWithinEditingWindowForYear(targetYear: number, referenceDate?: Date): boolean {
  return isWithinEditingWindow(referenceDate) && targetYear === getTargetProjectionYear(referenceDate);
}

/**
 * Get the projection status based on current date.
 * - Within the editing window for this year: 'draft'
 * - Otherwise: 'final'
 */
export function getProjectionStatusByDate(targetYear: number, referenceDate?: Date): ProjectionStatus {
  return isWithinEditingWindowForYear(targetYear, referenceDate) ? 'draft' : 'final';
}

/**
 * Get all available projection years for the archive dropdown.
 * Returns years from the first available projection to the current target.
 */
export function getAvailableProjectionYears(referenceDate?: Date): number[] {
  const targetYear = getTargetProjectionYear(referenceDate);
  const years: number[] = [];
  for (let year = FIRST_PROJECTION_YEAR; year <= targetYear; year++) {
    years.push(year);
  }
  return years;
}

/**
 * Check if an executive override is active and not expired
 */
export function isOverrideActive(metadata: ProjectionMetadata | null, referenceDate?: Date): boolean {
  if (!metadata?.unlockOverrideAt) return false;

  // Check if override has expired
  if (metadata.unlockOverrideExpiresAt) {
    const expiresAt = new Date(metadata.unlockOverrideExpiresAt);
    if ((referenceDate || new Date()) > expiresAt) return false;
  }

  return true;
}

/**
 * Editing is allowed while the window is open for THIS year, or while an
 * executive override is active on it. (The backend's canEdit is authoritative.)
 */
export function canEditProjection(
  metadata: ProjectionMetadata | null,
  targetYear: number,
  referenceDate?: Date
): boolean {
  if (isWithinEditingWindowForYear(targetYear, referenceDate)) return true;
  return isOverrideActive(metadata, referenceDate);
}

/**
 * Executive monthly adjustments are allowed on the current in-flight year and the upcoming
 * target year on any day, with no window or override; an archived year needs an active
 * Unlock-for-Corrections override. Mirrors lambda/shared/projection-types.ts.
 */
export function isYearOpenForMonthlyAdjustment(
  targetYear: number,
  metadata: ProjectionMetadata | null,
  referenceDate?: Date
): boolean {
  const now = referenceDate || new Date();
  const { year: currentYear } = easternDateParts(now);
  if (targetYear === currentYear || targetYear === getTargetProjectionYear(now)) return true;
  return isOverrideActive(metadata, now);
}

/**
 * Get the current month (1-12)
 */
export function getCurrentMonth(): number {
  return new Date().getMonth() + 1;
}

/**
 * Calculate annualized value from YTD data
 * Simple formula: YTD / (monthsElapsed / 12)
 */
export function calculateAnnualizedValue(ytdValue: number, monthsElapsed: number): number {
  if (monthsElapsed <= 0 || monthsElapsed > 12) {
    return ytdValue;
  }
  return Math.round(ytdValue / (monthsElapsed / 12));
}

/**
 * Annualize a partial year from its CLOSED months only: sum month1..monthsClosed
 * and scale by 12 / monthsClosed. The stored YTD total also includes the
 * in-progress month, so it must not be used as the numerator (a mid-October
 * YTD x 12/9 would overstate the year). Full or empty years return the sum of
 * all 12 months unchanged.
 */
export function annualizeClosedMonths(
  monthly: object | null | undefined,
  monthsClosed: number
): number {
  const values = monthly as Record<string, number> | null | undefined;
  const sumTo = (n: number) => {
    let total = 0;
    for (let m = 1; m <= n; m++) total += values?.[`month${m}`] || 0;
    return total;
  };
  if (monthsClosed <= 0 || monthsClosed >= 12) return sumTo(12);
  return Math.round(sumTo(monthsClosed) * 12 / monthsClosed);
}

/**
 * Calculate expense dollar amount from percentage of gross
 */
export function calculateExpenseFromPercentage(percentage: number, grossAmount: number): number {
  return Math.round((percentage / 100) * grossAmount);
}

/**
 * Sum monthly totals
 */
export function sumMonthlyTotals(totals: MonthlyTotals): number {
  return (
    totals.month1 + totals.month2 + totals.month3 + totals.month4 +
    totals.month5 + totals.month6 + totals.month7 + totals.month8 +
    totals.month9 + totals.month10 + totals.month11 + totals.month12
  );
}
